import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const containerStyle = {
  maxWidth: '1200px',
  margin: '0 auto',
  background: '#fff',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  padding: '24px',
  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
};

const tabsStyle = {
  display: 'flex',
  gap: '12px',
  marginBottom: '20px',
};

const tabButton = (active) => ({
  padding: '10px 18px',
  borderRadius: '999px',
  border: '1px solid',
  borderColor: active ? '#2563eb' : '#cbd5f5',
  background: active ? '#2563eb' : '#fff',
  color: active ? '#fff' : '#1f2937',
  cursor: 'pointer',
  fontWeight: active ? 700 : 500,
});

const listStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  overflow: 'hidden',
};

const headerStyle = {
  display: 'grid',
  gridTemplateColumns: '40px 150px 2fr 1fr 150px',
  background: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
  padding: '12px 16px',
  fontWeight: 600,
  color: '#475569',
  fontSize: '0.9rem',
};

const rowStyle = {
  display: 'grid',
  gridTemplateColumns: '40px 150px 2fr 1fr 150px',
  padding: '12px 16px',
  borderBottom: '1px solid #f1f5f9',
  alignItems: 'center',
  fontSize: '0.9rem',
};

const detailPanel = {
  marginTop: '20px',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  padding: '16px',
  background: '#fdf2f8',
};

function MailboxPage() {
  const [activeTab, setActiveTab] = useState('inbox');
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMail, setSelectedMail] = useState(null);
  const [selectedBox, setSelectedBox] = useState('inbox'); // 선택된 메일이 속한 박스(inbox/sent)
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false); // 기본값을 false로 변경 (수동 새로고침 권장)
  const [selectedIds, setSelectedIds] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [lastFetchTime, setLastFetchTime] = useState(0); // Rate Limiting용

  const fetchMailbox = async (skipRateLimit = false, silent = false) => {
    // ⬇️ Rate Limiting: 수동 새로고침 시에만 적용 (탭 전환/자동 새로고침 시에는 적용 안 함)
    if (!skipRateLimit) {
      const now = Date.now();
      const timeSinceLastFetch = now - lastFetchTime;
      const minInterval = 5000; // 5초

      if (timeSinceLastFetch < minInterval && lastFetchTime > 0) {
        const remainingTime = Math.ceil((minInterval - timeSinceLastFetch) / 1000);
        setError(`잠시 후 다시 시도해주세요. (${remainingTime}초)`);
        return;
      }
      setLastFetchTime(now);
    }

    // ⬇️ silent 모드: 백그라운드 갱신 시 로딩 표시 안 함
    if (!silent) {
      setLoading(true);
    }
    setError('');
    
    try {
      const response = await api.get('/mailbox', {
        params: { box: activeTab, keyword: keyword.trim() || undefined },
      });
      if (activeTab === 'inbox') {
        setInbox(response.data || []);
      } else {
        setSent(response.data || []);
      }
    } catch (err) {
      const message = err.response?.data?.message || '메일함을 불러오지 못했습니다.';
      setError(message);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // ⬇️ 탭 전환/검색 조건 변경 시: 이미 데이터가 있으면 즉시 표시하고 백그라운드에서 갱신
    const currentData = activeTab === 'inbox' ? inbox : sent;
    
    if (currentData.length > 0) {
      // 이미 데이터가 있으면 즉시 표시하고 백그라운드에서 조용히 갱신 (로딩 표시 안 함)
      // Rate Limiting 적용 안 함, 백엔드 캐싱 활용
      fetchMailbox(true, true); // skipRateLimit=true, silent=true
    } else {
      // 데이터가 없으면 로딩 표시하며 갱신
      fetchMailbox(true, false); // skipRateLimit=true, silent=false
    }
    
    setPage(1); // 탭/검색 조건이 바뀌면 첫 페이지로 이동
    if (!autoRefresh) return;
    // ⬇️ 자동 새로고침 간격을 3분에서 5분으로 변경 (Rate Limiting 적용 안 함, 조용히 갱신)
    const interval = setInterval(() => fetchMailbox(true, true), 1000 * 60 * 5);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, autoRefresh, keyword]);

  const mails = useMemo(() => (activeTab === 'inbox' ? inbox : sent), [activeTab, inbox, sent]);
  const totalPages = Math.max(1, Math.ceil(mails.length / pageSize));
  const pagedMails = useMemo(() => {
    const start = (page - 1) * pageSize;
    return mails.slice(start, start + pageSize);
  }, [mails, page, pageSize]);

  const formatDate = (value) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleString();
    } catch (error) {
      return value;
    }
  };

  const bulkUpdate = async (action) => {
    if (selectedIds.length === 0) return;
    try {
      setActionLoading(true);
      await api.post('/mailbox', {
        box: activeTab,
        ids: selectedIds,
        action,
      });
      setSelectedIds([]);
      if (selectedMail && selectedIds.includes(selectedMail.id)) {
        setSelectedMail(null);
      }
      fetchMailbox();
    } catch (err) {
      const message = err.response?.data?.message || '작업 중 오류가 발생했습니다.';
      setError(message);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(pagedMails.map((mail) => mail.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const isAllSelected = pagedMails.length > 0 && selectedIds.length === pagedMails.length;

  const loadMailDetail = async (mail, sourceBox) => {
    try {
      setDetailLoading(true);
      setError('');
      const box = sourceBox || activeTab;
      const response = await api.get('/mailbox/message', {
        params: { box, uid: mail.id },
      });
      setSelectedMail(response.data);
      setSelectedBox(box);
    } catch (err) {
      const message = err.response?.data?.message || '메일 내용을 불러오지 못했습니다.';
      setError(message);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDownloadAttachment = async (att, idx, event) => {
    event.preventDefault();
    // 임시 안내: 첨부 다운로드 기능 비활성화
    // eslint-disable-next-line no-alert
    alert(
      '이메일 서버 연결 이슈로 첨부파일 다운로드 기능을 일시 중단했습니다.\n\n' +
        '반송된 사용자는 내용에 기재된 이메일 주소로 확인하거나\n' +
        '네이버나 다음메일에서 직접 첨부파일을 내려받아 확인해 주세요.'
    );
  };

  const stickyBarStyle = {
    position: 'sticky',
    top: 60,
    zIndex: 5,
    background: '#ffffff',
    padding: '8px 0 12px 0',
    borderBottom: '1px solid #e5e7eb',
  };

  return (
    <div>
      <div style={stickyBarStyle}>
        <h2 style={{ marginBottom: '12px' }}>이메일 관리함</h2>
        <div style={tabsStyle}>
          <button
            style={tabButton(activeTab === 'inbox')}
            onClick={() => {
              setActiveTab('inbox');
              setSelectedMail(null);
              setSelectedBox('inbox');
              setSelectedIds([]);
              setPage(1);
            }}
          >
            수신함
          </button>
          <button
            style={tabButton(activeTab === 'sent')}
            onClick={() => {
              setActiveTab('sent');
              setSelectedMail(null);
              setSelectedBox('sent');
              setSelectedIds([]);
              setPage(1);
            }}
          >
            발신함
          </button>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
              자동 새로고침(5분)
            </label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  setKeyword(searchInput);
                }
              }}
              placeholder="제목 / 발신자 / 수신자 검색"
              style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5f5', minWidth: '230px' }}
            />
            <button
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5f5', background: '#f1f5f9' }}
              onClick={() => setKeyword(searchInput)}
              disabled={loading}
            >
              검색
            </button>
            <button
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5f5', background: '#fff' }}
              onClick={() => {
                setSearchInput('');
                setKeyword('');
              }}
            >
              초기화
            </button>
            <button
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5f5', background: '#fff' }}
              onClick={() => fetchMailbox(false)} // 수동 새로고침은 Rate Limiting 적용
              disabled={loading}
            >
              새로고침
            </button>
            <button
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #fecaca', background: '#fee2e2', color: '#b91c1c' }}
              onClick={() => bulkUpdate('delete')}
              disabled={loading || actionLoading || selectedIds.length === 0}
            >
              삭제
            </button>
            <button
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #bae6fd', background: '#e0f2fe', color: '#0369a1' }}
              onClick={() => bulkUpdate('read')}
              disabled={loading || actionLoading || selectedIds.length === 0}
            >
              읽음 처리
            </button>
            <button
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #ddd6fe',
                background: '#ede9fe',
                color: '#5b21b6',
              }}
              onClick={() => bulkUpdate('unread')}
              disabled={loading || actionLoading || selectedIds.length === 0}
            >
              읽지 않음
            </button>
          </div>
        </div>
      </div>
      <div style={containerStyle}>

        {error && (
          <div style={{ marginBottom: '12px', padding: '10px', borderRadius: '8px', background: '#fef2f2', color: '#b91c1c' }}>
            {error}
          </div>
        )}

        <div style={listStyle}>
          <div style={headerStyle}>
            <span>
              <input type="checkbox" checked={isAllSelected} onChange={(e) => toggleSelectAll(e.target.checked)} />
            </span>
            <span>{activeTab === 'inbox' ? '보낸 사람' : '받는 사람'}</span>
            <span>제목</span>
            <span>상태</span>
            <span>시간</span>
          </div>
          {loading ? (
            <div style={{ padding: '24px', textAlign: 'center' }}>불러오는 중...</div>
          ) : mails.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>메일이 없습니다.</div>
          ) : (
            pagedMails.map((mail) => (
              <div
                key={mail.id}
                style={{ ...rowStyle, cursor: 'pointer', background: selectedMail?.id === mail.id ? '#eef2ff' : '#fff' }}
                onClick={() => loadMailDetail(mail, activeTab)}
              >
                <span>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(mail.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleSelectOne(mail.id);
                    }}
                  />
                </span>
                <span>{activeTab === 'inbox' ? mail.from : mail.to}</span>
                <span style={{ fontWeight: mail.unread ? 600 : 400 }}>{mail.subject || '(제목 없음)'}</span>
                <span style={{ color: mail.error ? '#dc2626' : '#0f172a' }}>{mail.status || '-'}</span>
                <span>{formatDate(mail.date)}</span>
              </div>
            ))
          )}
        </div>

        {/* 페이지네이션 */}
        {mails.length > 0 && (
          <div
            style={{
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              border: '1px solid #e2e8f0',
              borderTop: 'none',
              borderRadius: '0 0 10px 10px',
              background: '#f9fafb',
              marginBottom: '16px',
            }}
          >
            <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
              총 {mails.length}건 · 페이지 {page} / {totalPages}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5f5',
                  background: page === 1 ? '#e5e7eb' : '#fff',
                  color: '#1f2937',
                  cursor: page === 1 ? 'default' : 'pointer',
                  fontSize: '0.85rem',
                }}
              >
                이전
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5f5',
                  background: page === totalPages ? '#e5e7eb' : '#fff',
                  color: '#1f2937',
                  cursor: page === totalPages ? 'default' : 'pointer',
                  fontSize: '0.85rem',
                }}
              >
                다음
              </button>
            </div>
          </div>
        )}

        {selectedMail && (
          <div style={detailPanel}>
            <div style={{ marginBottom: '10px', fontWeight: 600 }}>
              {selectedMail.subject || '(제목 없음)'}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#475569', marginBottom: '6px' }}>
              {activeTab === 'inbox' ? `보낸 사람: ${selectedMail.from}` : `받는 사람: ${selectedMail.to}`}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#475569', marginBottom: '6px' }}>
              상태: {selectedMail.status || '-'}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#475569', marginBottom: '12px' }}>
              시간: {formatDate(selectedMail.date)}
            </div>
            {detailLoading ? (
              <div style={{ padding: '12px' }}>내용 불러오는 중...</div>
            ) : (
              <>
                {selectedMail.attachments && selectedMail.attachments.length > 0 && (
                  <div style={{ marginBottom: '12px', fontSize: '0.85rem', color: '#475569' }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>첨부파일</div>
                    <ul style={{ paddingLeft: '18px', margin: 0 }}>
                      {selectedMail.attachments.map((att, idx) => (
                        <li key={`${att.partId || idx}-${att.filename}`}>
                          <button
                            type="button"
                            onClick={(e) => handleDownloadAttachment(att, idx, e)}
                            style={{
                              border: 'none',
                              background: 'none',
                              padding: 0,
                              margin: 0,
                              color: '#2563eb',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                          >
                            {att.filename}
                          </button>{' '}
                          ({att.contentType}, {att.size} bytes)
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <pre
                  style={{
                    background: '#fff',
                    border: '1px solid #fecdd3',
                    borderRadius: '8px',
                    padding: '12px',
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.9rem',
                    fontFamily: 'Pretendard, sans-serif',
                  }}
                >
                  {selectedMail.body || '(내용 없음)'}
                </pre>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default MailboxPage;


